import { Component, type ReactNode, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './core/auth/AuthContext.js';
import { rolProfiel } from './core/shell/rolProfiel.js';
import { ToastProvider } from './core/shared/components/Toast.js';
import { OfflineBanner } from './core/shared/components/OfflineBanner.js';
import { InstallBanner } from './core/shared/components/InstallBanner.js';
import { SyncQueueProvider } from './core/shared/components/SyncQueueProvider.js';
import { BrandProvider, useBrand } from './core/shared/components/BrandContext.js';
import { KordaroLogo } from './core/shared/components/KordaroLogo.js';
import { EntityBrandSync } from './core/shared/components/EntityBrandSync.js';
import { AppShell } from './core/shell/AppShell.js';
import { ImpersonationBanner } from './core/shell/ImpersonationBanner.js';
import { LoginPage } from './core/auth/LoginPage.js';
import { RegisterPage } from './core/auth/RegisterPage.js';
import { ForgotPasswordPage } from './core/auth/ForgotPasswordPage.js';
import { ResetPasswordPage } from './core/auth/ResetPasswordPage.js';
import { UitnodigingPublicPage } from './modules/buitendienst/pages/UitnodigingPublicPage.js';
import { KlantportaalPage } from './modules/klantportaal/pages/KlantportaalPage.js';
import { ProjectPortaalPage } from './modules/projecten/pages/ProjectPortaalPage.js';
import { SigningPage } from './modules/klantportaal/pages/SigningPage.js';
import { TevredenheidPage } from './modules/buitendienst/pages/TevredenheidPage.js';
import { AfspraakBevestigingPage } from './modules/planning/pages/AfspraakBevestigingPage.js';
import { SelfServiceBookingPage } from './modules/planning/pages/SelfServiceBookingPage.js';
import { ReferralPublicPage } from './modules/marketing/pages/ReferralPublicPage.js';
import { PubliekeFeedbackPage } from './modules/communicatie/pages/PubliekeFeedbackPage.js';
import { OnboardingRegistratiePage } from './modules/onboarding/pages/OnboardingRegistratiePage.js';
import { LandingPage } from './modules/marketing/pages/LandingPage.js';
import { LegalPage } from './modules/marketing/pages/LegalPage.js';
import { WinkelDashboard } from './core/shell/WinkelDashboard.js';
import { WinkelDossierDetail } from './core/shell/WinkelDossierDetail.js';
import { VorderingsstaatBeoordelen } from './core/shell/VorderingsstaatBeoordelen.js';

/** Redirect /dossiers/:id → /winkel/dossier/:id */
function DossierRedirect() {
  const { id } = useParams();
  return <Navigate to={`/winkel/dossier/${id}`} replace />;
}

/**
 * Role-aware landing redirect.
 *
 * Verkopers landen direct op hun Mijn Dag dashboard (stage-aware follow-up
 * prioriteiten) in plaats van het generieke Ixina-dashboard. Andere rollen
 * behouden het bestaande /dashboard als startpunt.
 *
 * Normalisatie (case-insensitive, beide User.role variants + StoreUserRole-
 * compat): 'VERKOPER' / 'verkoper' / 'sales' / 'SALES' → /crm/my-day.
 */
function RoleAwareLanding() {
  const { user } = useAuth();
  // Startscherm per rol (src/core/shell/rolProfiel.ts): verkopers (ook rol 'binnendienst') → Mijn Dag.
  return <Navigate to={rolProfiel(user?.role).start} replace />;
}
import { SystemDashboard } from './modules/admin/pages/SystemDashboard.js';
import { CSVImportPage } from './modules/onboarding/pages/CSVImportPage.js';
import { DossierLijstPage } from './core/shell/DossierLijstPage.js';
import { SettingsPage } from './core/settings/SettingsPage.js';
import { ImportPage } from './core/shell/ImportPage.js';
import { KlantenImportPage } from './core/shell/KlantenImportPage.js';
import { NotificatieCenterPage } from './modules/notificaties/pages/NotificatieCenterPage.js';
import { modules } from './modules/registry.js';

const LazyPlaatserModule = lazy(() => import('./modules/plaatser/index.js'));

const lazyModules = modules.map(m => ({
  ...m,
  LazyComponent: lazy(m.component),
}));

const SuspenseFallback = (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
  </div>
);

class GlobalErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace', maxWidth: 800, margin: '0 auto' }}>
          <h1 style={{ color: '#DC2626', fontSize: 20, marginBottom: 12 }}>Er ging iets mis</h1>
          <pre style={{ color: '#991B1B', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#FEF2F2', padding: 16, borderRadius: 8 }}>
            {this.state.error.message}
          </pre>
          <pre style={{ color: '#9CA3AF', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: 12 }}>
            {this.state.error.stack}
          </pre>
          <button
            onClick={() => { this.setState({ error: null }); window.location.href = '/'; }}
            style={{ marginTop: 16, padding: '8px 16px', background: 'var(--brand-primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Terug naar Home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Laadscherm vóór login: platformmerk Kordaro, of het logo van de winkel (whitelabel). */
function AppLaadScherm() {
  const brand = useBrand();
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center" role="status" aria-live="polite">
      <div className="text-center">
        {!brand.loading && (brand.logoUrl ? (
          <img src={brand.logoUrl} alt={brand.naam} className="mx-auto h-12 max-w-[200px] object-contain mb-6" />
        ) : brand.toontPlatformMerk ? (
          <div className="mb-6 flex justify-center"><KordaroLogo width={160} /></div>
        ) : null)}
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" aria-hidden="true" />
        <p className="text-gray-500">Laden...</p>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function AuthGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLaadScherm />;
  }

  if (!user) return <LoginPage />;
  return <>{children}</>;
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLaadScherm />;
  }

  if (!user) return <LoginPage />;

  // Plaatsers/opmeters/chauffeurs (intern én extern) → mobiele plaatsers-app
  if (['buitendienst', 'chauffeur', 'plaatser'].includes(user.role) || user.extern) {
    return <Navigate to="/app/plaatser" replace />;
  }

  return <AppShell />;
}

export default function App() {
  return (
    <GlobalErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrandProvider>
            <SyncQueueProvider>
            <OfflineBanner />
            <InstallBanner />
            <AuthProvider>
              <EntityBrandSync />
              <ImpersonationBanner />
              <Routes>
                {/* Public routes - geen auth nodig */}
                <Route path="/uitnodiging/:hash" element={<UitnodigingPublicPage />} />
                <Route path="/portaal/:token" element={<KlantportaalPage />} />
                <Route path="/projecten-portaal/:token" element={<ProjectPortaalPage />} />
                <Route path="/projecten/:id/portaal-preview/:lidId" element={<ProjectPortaalPage preview />} />
                <Route path="/portaal/:token/ondertekenen/:signingId" element={<SigningPage />} />
                <Route path="/tevredenheid/:token" element={<TevredenheidPage />} />
                <Route path="/afspraak/:hash" element={<AfspraakBevestigingPage />} />
                <Route path="/booking/:token" element={<SelfServiceBookingPage />} />
                <Route path="/referral/:code" element={<ReferralPublicPage />} />
                <Route path="/feedback/:token" element={<PubliekeFeedbackPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/onboarding" element={<OnboardingRegistratiePage />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                {/* Plaatsers App — eigen layout, geen sidebar */}
                <Route path="/app/plaatser/*" element={
                  <AuthGate>
                    <Suspense fallback={SuspenseFallback}>
                      <LazyPlaatserModule />
                    </Suspense>
                  </AuthGate>
                } />
                {/* Protected routes via AppContent layout */}
                <Route element={<AppContent />}>
                  <Route path="/winkel" element={<WinkelDashboard />} />
                  <Route path="/winkel/dossier/:id" element={<WinkelDossierDetail />} />
                  <Route path="/dossiers" element={<Navigate to="/buitendienst/dossiers" replace />} />
                  <Route path="/dossiers/:id" element={<DossierRedirect />} />
                  {/* Redirect oude dossier routes naar buitendienst module */}
                  <Route path="/vorderingsstaten/:id/beoordelen" element={<VorderingsstaatBeoordelen />} />
                  {/* /logistiek shell page (winkel logistiek overzicht) — sub-routes handled by logistiek module */}
                  <Route path="/import" element={<ImportPage />} />
                  <Route path="/notificaties" element={<NotificatieCenterPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/import" element={<KlantenImportPage />} />
                  <Route path="/admin/systeem" element={<SystemDashboard />} />
                  <Route path="/admin/import" element={<CSVImportPage />} />
                  {lazyModules.map(m => (
                    <Route key={m.id} path={`${m.basePath}/*`} element={
                      <Suspense fallback={SuspenseFallback}>
                        <m.LazyComponent />
                      </Suspense>
                    } />
                  ))}
                  <Route index element={<RoleAwareLanding />} />
                  <Route path="*" element={<RoleAwareLanding />} />
                </Route>
              </Routes>
            </AuthProvider>
            </SyncQueueProvider>
            </BrandProvider>
          </ToastProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </GlobalErrorBoundary>
  );
}
